import { Client, Room } from "colyseus.js";
import { GameState } from "@/lib/types/common/GameState";
import { GameMode } from "@/lib/types/common/GameMode";
import { Expansion } from "@/lib/types/common/Expansion";
import { MapType } from "@/lib/types/common/MapType";
import { getPlayerAvatar } from "@/lib/utils/avatarUtils";
import { GameModeAdvancedSettings, DEFAULT_GAME_MODE_SETTINGS } from "@/lib/types/common/GameModeAdvancedSettings";

// Default expansions when no settings saved (A Realm Reborn only)
const DEFAULT_EXPANSIONS = [Expansion.ARR];
// Default map types when no settings saved (Overworld only)
const DEFAULT_MAP_TYPES = [MapType.MAP];

const GAME_MODE_SETTINGS_STORAGE_KEY_PREFIX = 'eorguessr_gamemode_settings_';

export interface CreateRoomOptions {
	gameMode?: GameMode;
	[key: string]: unknown;
}

/**
 * Retrieves the saved advanced settings for a specific game mode from localStorage
 */
const getSavedGameModeSettings = (gameMode: GameMode): GameModeAdvancedSettings | undefined => {
	if (typeof window === 'undefined') return undefined;
	
	try {
		const storageKey = `${GAME_MODE_SETTINGS_STORAGE_KEY_PREFIX}${gameMode}`;
		const savedSettings = localStorage.getItem(storageKey);
		
		if (savedSettings) {
			const parsedSettings = JSON.parse(savedSettings);
			// Merge saved settings with defaults to ensure all required fields are present
			const defaultSettings = DEFAULT_GAME_MODE_SETTINGS[gameMode];
			const mergedSettings = {
				...defaultSettings,
				...parsedSettings,
				gameMode, // Ensure gameMode is always correct
			} as GameModeAdvancedSettings;
			return mergedSettings;
		}
	} catch (error) {
		console.warn(`Failed to load settings for ${gameMode}:`, error);
	}
	
	return undefined;
};

/**
 * Creates a new game room with player name from localStorage and optional additional options
 */
export const createGameRoom = async (
	client: Client,
	additionalOptions: CreateRoomOptions = {}
): Promise<Room<GameState>> => {
	const playerName = typeof window !== 'undefined' ? localStorage.getItem('playerName') : undefined;
	// Priority: stored from server > user selected > random
	const serverStoredAvatarId = typeof window !== 'undefined' ? localStorage.getItem('playerAvatarId') : undefined;
	const avatarId = serverStoredAvatarId || (typeof window !== 'undefined' ? getPlayerAvatar().id : undefined);
	// Get saved game mode for persistence on reload
	const savedGameMode = typeof window !== 'undefined' ? localStorage.getItem('eorguessr_selected_gamemode') as GameMode | null : null;
	// Get saved expansions and map types for persistence on reload
	// If no settings saved, use defaults (A Realm Reborn expansion, MAP/Overworld type)
	const savedExpansionsStr = typeof window !== 'undefined' ? localStorage.getItem('eorguessr_selected_expansions') : null;
	const savedMapTypesStr = typeof window !== 'undefined' ? localStorage.getItem('eorguessr_selected_maptypes') : null;
	const savedExpansions = savedExpansionsStr ? JSON.parse(savedExpansionsStr) as string[] : DEFAULT_EXPANSIONS;
	const savedMapTypes = savedMapTypesStr ? JSON.parse(savedMapTypesStr) as string[] : DEFAULT_MAP_TYPES;
	
	// Get saved advanced settings for the selected game mode
	const gameModeForSettings = additionalOptions.gameMode || savedGameMode;
	const savedAdvancedSettings = gameModeForSettings ? getSavedGameModeSettings(gameModeForSettings) : undefined;
	
	const options = {
		...(playerName ? { playerName } : {}),
		...(avatarId ? { avatarId } : {}),
		...(savedGameMode ? { gameMode: savedGameMode } : {}),
		selectedExpansions: savedExpansions,
		selectedMapTypes: savedMapTypes,
		...(savedAdvancedSettings ? { customSettings: savedAdvancedSettings } : {}),
		...additionalOptions
	};
	return await client.create("game_room", options) as Room<GameState>;
};

/**
 * Joins a room by ID with player name from localStorage and optional additional options
 */
export const joinGameRoom = async (
	client: Client,
	roomId: string,
	additionalOptions: CreateRoomOptions = {}
): Promise<Room<GameState>> => {
	const playerName = typeof window !== 'undefined' ? localStorage.getItem('playerName') : undefined;
	// Priority: stored from server > user selected > random
	const serverStoredAvatarId = typeof window !== 'undefined' ? localStorage.getItem('playerAvatarId') : undefined;
	const avatarId = serverStoredAvatarId || (typeof window !== 'undefined' ? getPlayerAvatar().id : undefined);
	const options = {
		...(playerName ? { playerName } : {}),
		...(avatarId ? { avatarId } : {}),
		...additionalOptions
	};
	return await client.joinById(roomId, options) as Room<GameState>;
};